/**
 * The panel's dates group: date first, then what happened, one row per event in the order they
 * happen (`patient-profile.mdx` → The dates). A row exists once its event has; Signed up and Consult
 * are the only ones every patient has.
 */

import { CallRowStatus } from '@bariendo/lib/intake-panel';
import { CoverageAction } from '@bariendo/lib/constants';
import { RecordedCoverageAction } from '@bariendo/lib/payment-status';
import { formatPanelDate } from './rows';

export interface DateFacts {
  signupDate?: string;
  consult: CallRowStatus;
  /** The consult's recorded outcome, already formatted; replaces "Occurred" once there is one. */
  consultOutcome: string;
  /** When the payment request was created: the order's own age. */
  requestedAt?: string;
  /** The coverage steps recorded against the active Coverage. */
  coverageActions: RecordedCoverageAction[];
  wonAt?: string;
  /** A cancelling request. Drawn only after a Won; before one, the Payment row says it. */
  cancellation?: { at: string; reason: string };
}

export interface DateRow {
  label: string;
  value: string;
}

/** The two steps on each coverage track between Consult and Won, under one pair of labels. */
const COVERAGE_STEPS: { label: string; actions: CoverageAction[] }[] = [
  { label: 'Referred', actions: [CoverageAction.REFERRAL_SENT] },
  { label: 'Submitted', actions: [CoverageAction.CASE_FORM_SUBMITTED, CoverageAction.PRIOR_AUTH_SUBMITTED] },
  { label: 'Approved', actions: [CoverageAction.APPROVED] },
];

function consultValue(consult: CallRowStatus, outcome: string): string {
  switch (consult.kind) {
    case 'none':
      return 'Not scheduled';
    case 'scheduled':
      return `${formatPanelDate(consult.date)} · Scheduled`;
    case 'occurred':
      return `${formatPanelDate(consult.date)} · ${outcome || 'Occurred'}`;
    case 'noshow':
      return `${formatPanelDate(consult.date)} · No-show`;
    case 'cancelled':
      return `${formatPanelDate(consult.date)} · Cancelled`;
  }
}

/** The step's latest record, by when it was recorded, as the payment status reads it. */
function latestOn(actions: RecordedCoverageAction[], codes: CoverageAction[]): string | undefined {
  return actions
    .filter((entry) => codes.includes(entry.action))
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))[0]?.on;
}

export function dateRows(facts: DateFacts): DateRow[] {
  const rows: DateRow[] = [];
  const push = (label: string, date: string | undefined, suffix?: string): void => {
    if (date) rows.push({ label, value: suffix ? `${formatPanelDate(date)} · ${suffix}` : formatPanelDate(date) });
  };

  push('Signed up', facts.signupDate);
  rows.push({ label: 'Consult', value: consultValue(facts.consult, facts.consultOutcome) });
  push('Requested', facts.requestedAt);
  for (const step of COVERAGE_STEPS) {
    push(step.label, latestOn(facts.coverageActions, step.actions));
  }
  push('Won', facts.wonAt);
  if (facts.wonAt && facts.cancellation) {
    push('Cancelled', facts.cancellation.at, facts.cancellation.reason);
  }

  return rows;
}
